import { useNavigate } from 'react-router-dom';
import customersStorageService from 'src/app/customers/services/customers-storage-service';
import projectsStorageService from 'src/app/projects/services/projects-storage-service';
import config from 'src/config';
import {
    AdminViews,
    EcologyHubViews,
    GNPViews,
    MapViews,
    ProjectViews,
    SamplesViews,
    SurveyViews,
    TableViews,
    SampleManifestViews,
    SampleReceptionViews,
    SampleManifestFormViews,
    HabitatInsightViews,
    PortfolioViews,
    SiteNatureRiskViews,
} from 'src/routes/routesConfig';
import { SortOrder } from 'src/shared/types';
import authStorageService from './services/auth-storage-service';
import { navRoutesConfig } from '../data/navRoutesConfig';
import { PortfolioSiteExplorerParams } from '../data/navRoutesConfig';

const useAppNavigation = () => {
    const navigate = useNavigate();
    const back = () => navigate(-1);

    const getCurrentCustomerId = () => customersStorageService.getCurrentCustomerId() || '';
    const getCurrentProjectId = () => projectsStorageService.getCurrentProjectId() || '';

    const getPath = (route: keyof typeof navRoutesConfig, params?: any) => {
        const routerBuilder = navRoutesConfig[route];
        if (typeof routerBuilder === 'string') {
            return routerBuilder;
        }
        return routerBuilder(params);
    };

    const handlers = {
        toHome: () => navigate(navRoutesConfig.home),
        toProjects: (params?: { customerId: string }) =>
            navigate(navRoutesConfig.projects({ customerId: params?.customerId || getCurrentCustomerId() })),
        toDashboard: (params?: { elementId?: string }) => {
            const path = navRoutesConfig.dashboard({ projectId: getCurrentProjectId(), customerId: getCurrentCustomerId() });
            const hash = params?.elementId ? `#${params.elementId}` : '';
            navigate(`${path}${hash}`);
        },
        toEcologyHub: () => navigate(navRoutesConfig.ecologyHub({ projectId: getCurrentProjectId(), customerId: getCurrentCustomerId() })),

        toTermsOfUse: () => navigate(navRoutesConfig.termsOfUse),
        toTermsOfUseDeclined: () => navigate(navRoutesConfig.termsOfUseDeclined),
        toImpersonateUser: () => navigate(navRoutesConfig.impersonateUser),

        toSampleMetricsTable: () =>
            navigate(
                navRoutesConfig[TableViews.SAMPLE_METRICS]({
                    projectId: getCurrentProjectId(),
                    customerId: getCurrentCustomerId(),
                })
            ),
        toHabitatInsightsMetricsTable: () =>
            navigate(
                navRoutesConfig[TableViews.HABITAT_INSIGHTS_METRICS]({
                    projectId: getCurrentProjectId(),
                    customerId: getCurrentCustomerId(),
                })
            ),

        toSpeciesMap: (params?: { projectId?: string; customerId?: string }) =>
            navigate(
                navRoutesConfig[MapViews.SPECIES_INSIGHTS_MAP]({
                    projectId: params?.projectId || getCurrentProjectId(),
                    customerId: params?.customerId || getCurrentCustomerId(),
                })
            ),
        toHabitatMap: (params?: { projectId?: string; customerId?: string }) =>
            navigate(
                navRoutesConfig[MapViews.HABITAT_INSIGHTS_MAP]({
                    projectId: params?.projectId || getCurrentProjectId(),
                    customerId: params?.customerId || getCurrentCustomerId(),
                })
            ),

        toOrgManagement: () => navigate(navRoutesConfig[AdminViews.GNP]),
        toUserManagement: () => navigate(navRoutesConfig[AdminViews.USER_MANAGEMENT]({ customerId: getCurrentCustomerId() })),
        toRiskPortfolioAdmin: () => navigate(navRoutesConfig[AdminViews.RISK_PORTFOLIO]({ customerId: getCurrentCustomerId() })),
        toEngineApiTokens: () => navigate(navRoutesConfig[AdminViews.ENGINE_API_TOKENS]),

        toSpeciesTrends: () =>
            navigate(
                navRoutesConfig[EcologyHubViews.SPECIES_TRENDS]({
                    projectId: getCurrentProjectId(),
                    customerId: getCurrentCustomerId(),
                })
            ),
        toHabitatTrends: () =>
            navigate(
                navRoutesConfig[EcologyHubViews.HABITAT_TRENDS]({
                    projectId: getCurrentProjectId(),
                    customerId: getCurrentCustomerId(),
                })
            ),
        toSampleComparison: () =>
            navigate(
                navRoutesConfig[EcologyHubViews.SAMPLE_COMPARISON]({
                    projectId: getCurrentProjectId(),
                    customerId: getCurrentCustomerId(),
                })
            ),
        toStatisticalComparison: () =>
            navigate(
                navRoutesConfig[EcologyHubViews.STATISTICAL_COMPARISON]({
                    projectId: getCurrentProjectId(),
                    customerId: getCurrentCustomerId(),
                })
            ),
        toTaxonomicComposition: () =>
            navigate(
                navRoutesConfig[EcologyHubViews.TAXONOMIC_COMPOSITION]({
                    projectId: getCurrentProjectId(),
                    customerId: getCurrentCustomerId(),
                })
            ),
        toCommunityComparison: () =>
            navigate(
                navRoutesConfig[EcologyHubViews.COMMUNITY_COMPARISON]({
                    projectId: getCurrentProjectId(),
                    customerId: getCurrentCustomerId(),
                })
            ),

        // Projects
        /** The organisation page under Admin > Management, which now administers projects. */
        toOrganisationManagement: (params?: { customerId?: string }) =>
            navigate(navRoutesConfig[GNPViews.ORG_MANAGEMENT]({ customerId: params?.customerId ?? getCurrentCustomerId() })),
        toProjectDefinition: () =>
            navigate(
                navRoutesConfig[ProjectViews.PROJECT_DEFINITION]({ customerId: getCurrentCustomerId(), projectId: getCurrentProjectId() })
            ),
        toNewProjectDefinition: () =>
            navigate(navRoutesConfig[ProjectViews.NEW_PROJECT_DEFINITION]({ customerId: getCurrentCustomerId() })),
        toEditProjectDefinition: (params?: { projectId?: string }) =>
            navigate(
                navRoutesConfig[ProjectViews.EDIT_PROJECT_DEFINITION]({
                    customerId: getCurrentCustomerId(),
                    projectId: params?.projectId || getCurrentProjectId(),
                })
            ),
        toFreemiumSpeciesInsights: () =>
            navigate(
                navRoutesConfig[ProjectViews.FREEMIUM_SPECIES_INSIGHTS]({
                    customerId: getCurrentCustomerId(),
                    projectId: getCurrentProjectId(),
                })
            ),
        toSiteFiles: (params?: { filterFileType?: string }) =>
            navigate(
                navRoutesConfig[ProjectViews.SITE_FILES]({
                    customerId: getCurrentCustomerId(),
                    projectId: getCurrentProjectId(),
                    filterFileType: params?.filterFileType,
                })
            ),
        toSiteSummary: (params?: { projectId?: string; customerId?: string }) =>
            navigate(
                navRoutesConfig[ProjectViews.SITE_SUMMARY]({
                    projectId: params?.projectId || getCurrentProjectId(),
                    customerId: params?.customerId || getCurrentCustomerId(),
                })
            ),

        toHabitatInsightsUpload: ({ projectId = '' } = {}) =>
            navigate(
                navRoutesConfig[HabitatInsightViews.HABITAT_INSIGHTS_UPLOAD]({
                    customerId: getCurrentCustomerId(),
                    projectId,
                })
            ),
        toAddSiteToPlatform: () => navigate(navRoutesConfig[HabitatInsightViews.HABITAT_INSIGHTS_ADD_SITE] as string),
        // Surveys
        toSurveyDesignSummary: () => navigate(navRoutesConfig[SurveyViews.SURVEY_DESIGN_SUMMARY]({ customerId: getCurrentCustomerId() })),
        toNewSurveyDesign: () => navigate(navRoutesConfig[SurveyViews.NEW_SURVEY_DESIGN]({ customerId: getCurrentCustomerId() })),
        toSurveyAdmin: () => navigate(navRoutesConfig[SurveyViews.SURVEY_ADMIN]({ customerId: getCurrentCustomerId() })),

        // Samples
        toSampleManifest: () =>
            navigate(
                navRoutesConfig[SamplesViews.SAMPLE_MANIFEST]({
                    projectId: getCurrentProjectId(),
                    customerId: getCurrentCustomerId(),
                })
            ),

        toSampleManifestForm: (params: { view?: SampleManifestFormViews; samplingEventId: string; batchId?: string }) =>
            navigate(
                navRoutesConfig[SampleManifestViews.FORM]({
                    projectId: getCurrentProjectId(),
                    customerId: getCurrentCustomerId(),
                    view: params?.view || SampleManifestFormViews.ADD_FIELD_DATA,
                    samplingEventId: params.samplingEventId,
                    batchId: params.batchId,
                })
            ),
        toSampleManifestView: (params: { samplingEventId: string }) =>
            navigate(
                navRoutesConfig[SampleManifestViews.VIEW]({
                    projectId: getCurrentProjectId(),
                    customerId: getCurrentCustomerId(),
                    samplingEventId: params.samplingEventId,
                })
            ),

        toSampleReception: () => navigate(navRoutesConfig[SampleReceptionViews.HOME]),
        toViewSampleManifest: (params: { projectId: string; customerId: string; samplingEventId: string }) =>
            navigate(
                navRoutesConfig[SampleReceptionViews.VIEW_SAMPLE_MANIFEST]({
                    projectId: params.projectId,
                    customerId: params.customerId,
                    samplingEventId: params.samplingEventId,
                })
            ),
        toLogin: () => {
            const { cognitoLoginUrl, cognitoClientId } = config;
            const locationOrigin = window.location.origin;
            const cognitoRedirectUrl = `${locationOrigin}/oauth?provider=aws`;
            const staticParams = `client_id=${cognitoClientId}&response_type=code&scope=aws.cognito.signin.user.admin`;
            const cognitoUrl = `${cognitoLoginUrl}login?${staticParams}&redirect_uri=${encodeURIComponent(cognitoRedirectUrl)}`;
            authStorageService.clearAll();
            window.location.href = cognitoUrl;
        },
        toUpgradeSubscription: () =>
            navigate(navRoutesConfig.upgradeSubscription({ projectId: getCurrentProjectId(), customerId: getCurrentCustomerId() })),
        toAddPasskey: () => {
            const { cognitoLoginUrl, cognitoClientId } = config;
            const locationOrigin = window.location.origin;
            const cognitoRedirectUrl = `${locationOrigin}/projects`;
            const staticParams = `client_id=${cognitoClientId}&scope=aws.cognito.signin.user.admin`;
            const cognitoUrl = `${cognitoLoginUrl}passkeys/add?${staticParams}&redirect_uri=${encodeURIComponent(cognitoRedirectUrl)}`;
            authStorageService.clearAll();
            window.location.href = cognitoUrl;
        },

        toPortfolioSummary: (customerId?: string) =>
            navigate(navRoutesConfig[PortfolioViews.PORTFOLIO_SUMMARY]({ customerId: customerId || getCurrentCustomerId() })),

        toPortfolioSpeciesRiskIndicators: (params?: { tab?: 'iucn-red-list-species' | 'invasive-species'; sortDirection?: SortOrder }) =>
            navigate(
                navRoutesConfig[PortfolioViews.SPECIES_RISK_INDICATORS]({
                    customerId: getCurrentCustomerId(),
                    tab: params?.tab,
                    sortDirection: params?.sortDirection,
                })
            ),
        toPortfolioSiteExplorer: (params?: PortfolioSiteExplorerParams) =>
            navigate(navRoutesConfig[PortfolioViews.PORTFOLIO_SITE_EXPLORER](params || { customerId: getCurrentCustomerId() })),

        // No sort param yet, unlike the live page: nothing links here with a sort to
        // apply, and the page's default order is already the one the links would ask for.
        toNextPortfolioSpeciesRiskIndicators: (params?: { tab?: 'iucn-red-list-species' | 'invasive-species' }) =>
            navigate(
                navRoutesConfig[PortfolioViews.NEXT_SPECIES_RISK_INDICATORS]({
                    customerId: getCurrentCustomerId(),
                    tab: params?.tab,
                })
            ),

        toNextPortfolioSiteExplorer: () =>
            navigate(navRoutesConfig[PortfolioViews.NEXT_PORTFOLIO_SITE_EXPLORER]({ customerId: getCurrentCustomerId() })),

        toNextPortfolioSummary: () =>
            navigate(navRoutesConfig[PortfolioViews.NEXT_PORTFOLIO_SUMMARY]({ customerId: getCurrentCustomerId() })),

        toGlobalNatureRiskOverview: () =>
            navigate(
                navRoutesConfig[PortfolioViews.GLOBAL_NATURE_RISK_OVERVIEW]({
                    customerId: getCurrentCustomerId(),
                })
            ),
        globalNatureRiskOverviewPath: navRoutesConfig[PortfolioViews.GLOBAL_NATURE_RISK_OVERVIEW]({
            customerId: getCurrentCustomerId(),
        }),
        toGlobalNatureRiskStateOfNature: () =>
            navigate(
                navRoutesConfig[PortfolioViews.GLOBAL_NATURE_RISK_STATE_OF_NATURE]({
                    customerId: getCurrentCustomerId(),
                })
            ),
        toGlobalNatureRiskSiteList: () =>
            navigate(
                navRoutesConfig[PortfolioViews.GLOBAL_NATURE_RISK_SITE_LIST]({
                    customerId: getCurrentCustomerId(),
                })
            ),
        toGlobalNatureRiskImpactsAndDependencies: () =>
            navigate(
                navRoutesConfig[PortfolioViews.GLOBAL_NATURE_RISK_IMPACTS_AND_DEPENDENCIES]({
                    customerId: getCurrentCustomerId(),
                })
            ),
        toPortfolioFiles: () =>
            navigate(
                navRoutesConfig[PortfolioViews.PORTFOLIO_FILES]({
                    customerId: getCurrentCustomerId(),
                })
            ),

        toSiteNatureRiskOverview: (siteId: string) =>
            navigate(navRoutesConfig[SiteNatureRiskViews.OVERVIEW]({ siteId, customerId: getCurrentCustomerId() })),
        toSiteNatureRiskCharacteristics: (siteId: string) =>
            navigate(navRoutesConfig[SiteNatureRiskViews.CHARACTERISTICS]({ siteId, customerId: getCurrentCustomerId() })),
        toSiteNatureRiskStateOfNature: (siteId: string) =>
            navigate(navRoutesConfig[SiteNatureRiskViews.STATE_OF_NATURE]({ siteId, customerId: getCurrentCustomerId() })),
        toSiteNatureRiskImpactsAndDependencies: (siteId: string) =>
            navigate(navRoutesConfig[SiteNatureRiskViews.IMPACTS_AND_DEPENDENCIES]({ siteId, customerId: getCurrentCustomerId() })),
    };

    return {
        to: navigate,
        getPath,
        back,
        ...handlers,
    };
};

export default useAppNavigation;
